/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@nextui-org/react'
import toast from 'react-hot-toast';
import moment from 'moment';
import { Constants, formatAmount, getCurrencySymbol, getOnlyNumbers } from '../../utils';
import { PaymentService } from '../../services';
import Render from '../../components/render';

const MobilePayment = ({ onClose, onSuccess, request, type = Constants.PAYMENT_TYPE.ACQUIRE }) => {
  const { banks, form, isLoading, onChange, paymentMethod, onSubmit } = useForm(request, onClose, onSuccess, type);

  const receptor = useMemo(() => {
    return paymentMethod?.info?.find(x => x.id === +form.receptor_id);
  }, [form.receptor_id]);

  return (
    <Modal size="md" isOpen={true} onClose={onClose}>
      <ModalContent>
        <ModalHeader className="flex flex-col">Pago Móvil</ModalHeader>
        <ModalBody className="py-0">
          <p className="text-center font-semibold text-2xl">
            { formatAmount(form.amount_local, form.currency_symbol) }
          </p>

          <div className="my-4">
            <Render condition={!!receptor}>
              <div className="text-blue text-center mb-2">
                <p>{ receptor?.bank?.code } - { receptor?.bank?.name }</p>
                <p>{ receptor?.dni }</p>
                <p>{ receptor?.phone }</p>
              </div>
              <Divider />
            </Render>

            <Render condition={!receptor}>
              <p className="text-center text-sm text-red">Seleccione un banco receptor para mostrar los datos de pago</p>
            </Render>
          </div>

          <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit}>
            <Select
              label="Banco receptor"
              labelPlacement="outside"
              placeholder=" "
              variant="bordered"
              radius="sm"
              classNames={{ base: 'col-span-2', label: 'font-semibold' }}
              disallowEmptySelection={true}
              selectedKeys={!!form.receptor_id ? [form.receptor_id] : []}
              onSelectionChange={v => onChange(v.currentKey, 'receptor_id')}
            >
              {paymentMethod?.info?.map((item) => (
                <SelectItem key={item.id} value={item.id}>{ item.label }</SelectItem>
              ))}
            </Select>
            <Select
              label="Banco emisor"
              labelPlacement="outside"
              placeholder=" "
              variant="bordered"
              radius="sm"
              classNames={{ base: 'col-span-2', label: 'font-semibold' }}
              disallowEmptySelection={true}
              selectedKeys={!!form.bank_id ? [form.bank_id] : []}
              onSelectionChange={v => onChange(v.currentKey, 'bank_id')}
            >
              {banks.map((item) => (
                <SelectItem key={item.value} value={item.value}>{ item.label }</SelectItem>
              ))}
            </Select>
            <Input
              type="date"
              label="Fecha de pago"
              labelPlacement="outside"
              placeholder=" "
              variant="bordered"
              radius="sm"
              max={moment().format('YYYY-MM-DD')}
              value={form.date}
              onValueChange={v => onChange(v, 'date')}
            />
            <Input
              label="Teléfono"
              labelPlacement="outside"
              placeholder=" "
              variant="bordered"
              radius="sm"
              value={form.phone}
              onValueChange={v => onChange(getOnlyNumbers(v), 'phone')}
            />
            <Input
              label="Referencia"
              labelPlacement="outside"
              placeholder=" "
              variant="bordered"
              radius="sm"
              classNames={{ base: 'col-span-2' }}
              value={form.reference}
              onValueChange={v => onChange(getOnlyNumbers(v), 'reference')}
            />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" className="bg-green" isLoading={isLoading} onPress={onSubmit}>
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const useForm = (request, onClose, onSuccess, type) => {
  const initialForm = {
    payment_method_id: '',
    amount: request?.amount ?? 0,
    amount_local: 0,
    currency_symbol: '',
    receptor_id: '',
    bank_id: '',
    date: '',
    phone: '',
    reference: '',
    conversion: 1,
  };

  const [form, setForm] = useState(initialForm);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [banks, setBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getSelects = () => {
    setIsLoading(true);

    Promise.all([
      PaymentService.payer.getConversion({ user_id: request?.user.id }),
      PaymentService.payer.getBanks({ user_id: request?.user.id }),
      PaymentService.payer.getMethods({ user_id: request?.user.id }),
    ]).then(res => {
      const country_id = res[2][0].country_id;
      // Establece la tasa de cambio y el valor en moneda local
      const conversion = res[0].amount;
      const amount_local = parseFloat(parseFloat(form.amount * conversion).toFixed(2));
      let currency_symbol = getCurrencySymbol(country_id);
      let payment_method_id = '';

      // Establece el país para definir el método de pago en base a este
      switch (country_id) {
        case Constants.COUNTRIES.VEN:
          payment_method_id = Constants.PAYMENT_METHODS.VE_MOBILE;
          break;

        case Constants.COUNTRIES.COL:
          payment_method_id = Constants.PAYMENT_METHODS.CO_MOBILE;
          break;

        default: break;
      }

      setForm(s => ({ ...s, conversion, amount_local, currency_symbol, payment_method_id }));
      // Establece los bancos en el select
      setBanks(res[1].map(x => ({ ...x, value: String(x.id), label: (x.code ? x.code + ' - ' : '') + x.name })));
      // Busca el método de pago indicado para mostrar los datos de pago
      const pm = res[2].find(x => x.id === payment_method_id);
      pm?.info?.forEach(x => {
        x.value = String(x.id);
        x.label = x.bank?.code  + ' - ' + x.bank?.name;
      });
      setPaymentMethod(pm);
    })
    .catch(console.log)
    .finally(() => setIsLoading(false));
  }

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    }

    if (!form.receptor_id)
      return onError('Debe seleccionar el banco receptor');

    if (!form.bank_id)
      return onError('Debe seleccionar el banco emisor');

    if (!form.date)
      return onError('Debe indicar la fecha de pago');

    if (!form.phone)
      return onError('Debe indicar el número de teléfono');

    if (!form.reference)
      return onError('Debe indicar la referencia del pago');

    return true;
  }

  const onSubmit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);

    try {
      const payment = {
        ...form,
        date: form.date ? moment(form.date).format('YYYY-MM-DD') : null,
      };

      const _user_id = request?.user.id;
      const res = (type === Constants.PAYMENT_TYPE.ACQUIRE)
        ? await PaymentService.payer.acquirePlan({ _user_id, payment, request })
        : await PaymentService.payer.renewPlan({ _user_id, payment, request });

      toast.success('Pago exitoso');
      if (onSuccess) onSuccess(res);
      if (onClose) onClose();

    } catch (error) {
      return null;

    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getSelects();
  }, []);

  return {
    banks,
    form,
    isLoading,
    onChange,
    paymentMethod,
    onSubmit,
  }
}

export default MobilePayment
